/**
 * usePublishAction — THE publish machine. Every publish surface (the hub's
 * Overview {@link PublishControl} and the studio card's corner button) runs
 * this one hook; there is no second `updateCreation` wiring anywhere.
 *
 * Publish IS the existing `unlisted→public` visibility flip via updateCreation
 * (no new callable); Unpublish is the reverse. Members (creator + editors) flip
 * directly; a non-member superadmin rides the attribution modal like every
 * other creation mutation (render `attributionModal` in the surface's JSX).
 *
 * The three visibility states (see `publishState`):
 *  - `unlisted` → "Unpublished", offers Publish. Copy must stay honest:
 *    unlisted means NOT LISTED in the gallery — anyone with the link can play
 *    (RLS reads non-draft rows publicly), never "hidden"/"private".
 *  - `public`   → "Published", offers Unpublish.
 *  - `draft`    → the platform-managed pre-divergence fork state: NOBODY can
 *    flip it client-side (the builder's divergence is the only exit), so the
 *    hook offers NO action that would only ever fail.
 *
 * PENDING-AUDIT UX: going public requires the ACTIVE version to carry the
 * Tier-2 playtest-audit stamp. When it doesn't, the server auto-enqueues the
 * audit and rejects with the typed `audit-required` error
 * (`functions/failed-precondition`, `details.jobId`) — a PENDING phase, not a
 * failure:
 *  - SUCCESS signal: the stamp lands on the active VERSION doc's
 *    `gates.playtestAudit` (stamped before the job is marked succeeded). On
 *    arrival the hook auto-retries the publish ONCE (reusing the attribution
 *    already collected); if that retry bounces again it falls back to a
 *    one-click "publish-now".
 *  - FAILURE signal: the audit job flipping to `status:'failed'` — a failed job
 *    never stamps, so without this the pending state would spin forever.
 *  - MOVED-POINTER signal: the job flipping to `status:'succeeded'` while the
 *    ACTIVE pointer no longer sits on the version it audited (a new build
 *    shipped mid-audit). Its stamp landed on the OLD version, so the success
 *    signal above would never fire — resolves to `stale-audit`, a one-click
 *    "publish-now" that enqueues a fresh audit for the now-active version.
 *
 * Both watches are PENDING-ONLY: `stamped` and the job outcome are consulted
 * exclusively while an audit is in flight, so the two subscriptions open with
 * the pending phase and close with it. That is what lets a compact surface —
 * a studio card in a grid of N — run the identical machine without holding a
 * versions stream per card at rest, and it is why the hook takes only the
 * creation (no versions prop to drill).
 */
import { useEffect, useRef, useState, type ReactNode } from 'react';
import { titleHygieneWarning, type AuditJob, type Creation, type CreationVersion } from '@djgg/creations';
import { useServices } from '../services/context.js';
import { useSubscription } from '../hooks/useSubscription.js';
import { useAttributedAction } from '../hooks/useAttributedAction.js';
import { publishState, type PublishState } from './publishState.js';

/** The `audit-required` rejection as the CLIENT sees it: the firebase callable
 *  error carries code `functions/failed-precondition` and the handler's
 *  `{ jobId, deduped }` in `details`. Null = some other failure. */
function auditRequiredDetails(e: unknown): { jobId: string; deduped: boolean } | null {
  const err = e as { code?: unknown; details?: unknown } | null;
  if (!err || err.code !== 'functions/failed-precondition') return null;
  const details = err.details as { jobId?: unknown; deduped?: unknown } | undefined;
  return typeof details?.jobId === 'string'
    ? { jobId: details.jobId, deduped: details.deduped === true }
    : null;
}

export type PublishPhase =
  | { kind: 'idle' }
  | { kind: 'busy' }
  /** Audit in flight. `autoRetried` = the one automatic re-publish was already
   *  spent (a second stamp-arrival offers one-click instead of looping). */
  | { kind: 'pending'; jobId: string; autoRetried: boolean }
  /** The audit SUCCEEDED but for a version the active pointer moved off of
   *  mid-pending — its stamp can never satisfy the current active version, so
   *  pending resolves to a one-click publish (a fresh audit for the new active
   *  version rides the next attempt). */
  | { kind: 'stale-audit' }
  | { kind: 'audit-failed'; message: string }
  | { kind: 'error'; message: string };

/** What the ONE action button does right now. The DECISION lives here so the
 *  hub and the card can never offer different actions; the WORDS live in each
 *  surface (the hub spells "Publish to the gallery", the card "Publish"). */
export type PublishActionKind = 'publish' | 'publish-now' | 'retry-publish' | 'unpublish';

export interface PublishAction {
  /** The creator-facing visibility state (the chip/label vocabulary). */
  state: PublishState;
  /** Where the publish episode currently stands. */
  phase: PublishPhase;
  /** A flip callable is in flight (disable the button, say "Working…"). */
  busy: boolean;
  /** The Tier-2 stamp sits on the ACTIVE version (only watched while pending). */
  stamped: boolean;
  /** The action to offer, or null when this state offers none. */
  action: PublishActionKind | null;
  /** Run the offered action; a no-op when `action` is null. */
  run: () => void;
  /** Mount in the surface's JSX so the attribution pick can appear. */
  attributionModal: ReactNode;
  /** Advisory only (owner Decision 267): the title reads like a working or
   *  test name. Set only while a publish action is offered; never blocks it. */
  titleWarning: string | null;
}

export function usePublishAction(creation: Creation & { id: string }): PublishAction {
  const services = useServices();
  const { runAttributed, attributionModal } = useAttributedAction();
  const [phase, setPhase] = useState<PublishPhase>({ kind: 'idle' });
  // The attribution resolved for the CURRENT publish episode, so the automatic
  // retry continues the same attributed action instead of re-prompting.
  const attributionRef = useRef<string | undefined>(undefined);

  const state = publishState(creation.visibility);
  const pendingJobId = phase.kind === 'pending' ? phase.jobId : null;

  // The stamp watch: the active version doc carries the Tier-2 gate.
  const versions = useSubscription<Array<CreationVersion & { id: string }>>(
    (cb, onError) =>
      pendingJobId ? services.subscribe.versions(creation.id, cb, onError) : () => {},
    [],
    [creation.id, pendingJobId],
  );
  const activeDoc = versions.find((v) => v.n === (creation.activeVersion ?? 0)) ?? null;
  const stamped = !!activeDoc?.gates?.playtestAudit?.tier2;

  // The failed/moved-pointer watch.
  const auditJobs = useSubscription<Array<AuditJob & { id: string }>>(
    (cb, onError) =>
      pendingJobId ? services.subscribe.auditJobs(creation.id, cb, onError) : () => {},
    [],
    [creation.id, pendingJobId],
  );

  /** The flip itself, with attribution already resolved. */
  async function flip(visibility: 'public' | 'unlisted', autoRetried: boolean): Promise<void> {
    setPhase({ kind: 'busy' });
    try {
      await services.callable.updateCreation({
        creationId: creation.id,
        visibility,
        attributeTo: attributionRef.current,
      });
      // The creation subscription delivers the new visibility; nothing to hold.
      setPhase({ kind: 'idle' });
    } catch (e) {
      const audit = visibility === 'public' ? auditRequiredDetails(e) : null;
      if (audit) {
        setPhase({ kind: 'pending', jobId: audit.jobId, autoRetried });
      } else {
        setPhase({ kind: 'error', message: e instanceof Error ? e.message : 'The change failed.' });
      }
    }
  }

  // Stamp landed while pending → the ONE automatic publish retry (the same
  // attributed action continuing, so no re-prompt).
  useEffect(() => {
    if (phase.kind === 'pending' && !phase.autoRetried && stamped) {
      void flip('public', true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- phase/stamped drive it
  }, [phase, stamped]);

  // Audit job resolution — the two outcomes that will never deliver a stamp
  // for the CURRENT active version, so pending must exit EXPLICITLY:
  //  - 'failed': a failed job never stamps.
  //  - 'succeeded' for a version the active pointer MOVED OFF OF mid-pending
  //    (a new build went live): the stamp landed on the OLD version, so the
  //    stamp watch above would spin forever. A succeeded job for the
  //    still-active version needs nothing here — its stamp was written before
  //    the status flip and arrives through the versions subscription, firing
  //    the auto-retry above.
  useEffect(() => {
    if (phase.kind !== 'pending') return;
    const job = auditJobs.find((j) => j.id === phase.jobId);
    if (job?.status === 'failed') {
      setPhase({ kind: 'audit-failed', message: job.error ?? '' });
    } else if (
      job?.status === 'succeeded' &&
      !stamped &&
      job.version !== (creation.activeVersion ?? 0)
    ) {
      setPhase({ kind: 'stale-audit' });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- phase/auditJobs/stamped/activeVersion drive it
  }, [phase, auditJobs, stamped, creation.activeVersion]);

  const action: PublishActionKind | null =
    state === 'published'
      ? 'unpublish'
      : state === 'private-fork'
        ? null
        : phase.kind === 'pending'
          ? // Pending owns the surface; the one-click only returns once the
            // automatic retry is spent and the stamp is in.
            stamped && phase.autoRetried
            ? 'publish-now'
            : null
          : phase.kind === 'stale-audit'
            ? 'publish-now'
            : phase.kind === 'audit-failed'
              ? 'retry-publish'
              : 'publish';

  return {
    state,
    phase,
    busy: phase.kind === 'busy',
    stamped,
    action,
    run: () => {
      if (!action) return;
      const visibility = action === 'unpublish' ? 'unlisted' : 'public';
      /** Button entry: collect attribution (superadmin non-member) then flip. */
      void runAttributed(creation, async (attributeTo) => {
        attributionRef.current = attributeTo;
        await flip(visibility, false);
      });
    },
    attributionModal,
    titleWarning: action && action !== 'unpublish' ? titleHygieneWarning(creation.title) : null,
  };
}
